import {
	changeMarkupOnHide,
	changeMarkupOnShow,
} from 'fukku/Modal/helpers/changeMarkup'
import { type RefObject, useEffect, useState } from 'react'

import styles from '../../Sheet.module.scss'

const HEIGHT_AUTO = 0
const HIDDEN_OFFSET = 0

export function useSheet(
	open: boolean,
	containerRef: RefObject<HTMLDivElement>,
	headerRef: RefObject<HTMLDivElement>,
	footerRef: RefObject<HTMLDivElement>,
	isDraggingRef: RefObject<boolean>
) {
	const [opened, setOpened] = useState(false)
	const [offset, setOffset] = useState(0)
	const [height, setHeight] = useState(0)
	const [contentHeight, setContentHeight] = useState(0)
	const [restoreHeight, setRestoreHeight] = useState(0)
	const [footerMargin, setFooterMargin] = useState(0)

	useEffect(() => {
		const resizeObserver = new ResizeObserver(() => {
			if (opened) {
				const headerHeight = headerRef?.current?.clientHeight ?? 0
				const footerHeight = footerRef?.current?.clientHeight ?? 0
				const containerHeight = containerRef?.current?.clientHeight ?? 0
				const DRAGGING_ZONE_HEIGHT = 20
				// When is not dragging and change the height of the content
				if (isDraggingRef && !isDraggingRef.current) {
					setRestoreHeight(containerHeight)
				}
				setFooterMargin(footerHeight)
				setContentHeight(
					containerHeight - headerHeight - footerHeight - DRAGGING_ZONE_HEIGHT
				)
			}
		})
		if (containerRef?.current) {
			resizeObserver.observe(containerRef.current)
		}
		return () => {
			if (containerRef?.current) {
				resizeObserver.unobserve(containerRef.current)
				resizeObserver.disconnect()
			}
		}
	}, [opened, containerRef?.current])

	useEffect(() => {
		if (open) {
			// have to set as opened
			setOpened(true)
		}
		if (!open && opened && containerRef?.current) {
			// have to close with animation
			containerRef.current?.classList.add(styles.closing)
			containerRef.current.addEventListener(
				'animationend',
				() => {
					setOpened(false)
					containerRef.current?.classList.remove(styles.closing)
					changeMarkupOnHide()
				},
				{ once: true }
			)
		}
		return () => {
			changeMarkupOnHide()
		}
	}, [open])

	useEffect(() => {
		if (height === restoreHeight && containerRef?.current) {
			containerRef.current?.classList.add(styles.bounce)
			containerRef.current.addEventListener(
				'transitionend',
				() => {
					setOffset(HIDDEN_OFFSET)
					setHeight(HEIGHT_AUTO)
				},
				{ once: true }
			)
		}
		if (height === HEIGHT_AUTO) {
			containerRef.current?.classList.remove(styles.bounce)
		}
	}, [height, restoreHeight])

	useEffect(() => {
		if (opened) {
			if (containerRef?.current) {
				containerRef.current.classList.add(styles.opening)
				containerRef.current.addEventListener(
					'animationend',
					() => containerRef.current?.classList.remove(styles.opening),
					{ once: true }
				)
				changeMarkupOnShow()
			}
		} else {
			setOffset(HIDDEN_OFFSET)
			setHeight(HEIGHT_AUTO)
		}
	}, [opened])

	return {
		opened,
		offset,
		height,
		contentHeight,
		footerMargin,
		restoreHeight,
		setHeight,
		setOffset,
	}
}
